<template>
  <modal
    :value="isShown"
    :validation="validation"
    size="small"
    @close="onClose"
    @submit="onSubmit"
  >
    <template #title>
      Verify Your {{ shippingAccount.name }} Account
    </template>
    <template #body>
      <div class="flex flex-col w-full">
        <template v-if="loading">
          <div
            class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"
          />
        </template>
        <Alert
          v-if="message"
          :type="messageStatus"
          :close="true"
          class="mb-4"
          @close="$emit('clear-message')"
        >
          {{ message }}
        </Alert>
        <z-input
          id="pin"
          v-model:value="pinCode"
          :timeout="0"
          class="mb-4 w-full"
          placeholder="Enter PIN code"
          autofocus
        >
          <template #label>
            PIN Code
          </template>
        </z-input>

        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-600">
              Didn't receive your PIN?
            </span>
            <z-button
              v-if="!showResendOptions"
              class="text-sm text-blue-600 hover:text-blue-800 font-medium"
              @click="showResendOptions = true"
            >
              Resend PIN
            </z-button>
          </div>

          <div v-if="showResendOptions">
            <z-select
              id="pin_method"
              v-model:value="selectedPinMethod"
              :options="pinMethods"
              class="mb-4 w-full"
            >
              <template #label>
                PIN Methods
              </template>
            </z-select>

            <div class="flex gap-2 mt-3">
              <z-button
                size="small"
                @click="showResendOptions = false">
                Cancel
              </z-button>
              <z-button
                size="small"
                :disabled="!selectedPinMethod"
                @click="resendPin"
              >
                Send New PIN
              </z-button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <z-button
        full-width
        submit
        :loading="loading">
        Verify PIN
      </z-button>
    </template>
  </modal>
</template>

<script>
export default {
  name: "VerificationModal",
};
</script>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { number, object } from "yup";
import Modal from "../../organisms/Modal";
import ZInput from "../../atoms/Input";
import ZButton from "../../atoms/Button";
import ZSelect from "@/components/atoms/Select/index.vue";
import Alert from "@/components/atoms/Alert/index.vue";

const props = defineProps({
  isShown: {
    type: Boolean,
    default: true,
  },
  shippingAccount: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
  message: {
    type: String,
    default: "",
  },
  messageStatus: {
    type: String,
    default: "info",
  },
});

const emit = defineEmits([
  "submit",
  "close",
  "resend-pin-code",
  "clear-message",
]);

const pinCode = ref("");
const showResendOptions = ref(false);
const selectedPinMethod = ref("");

const pinMethods = [
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
  { value: "call", label: "Phone Call" },
];

const validation = object({
  pin: number().required().min(6).label("PIN Code"),
});

const onSubmit = () => {
  if (pinCode.value) {
    emit("submit", {
      id: props.shippingAccount && props.shippingAccount.id,
      carrier: "fedex_v2",
      auth: { pin: pinCode.value },
      name: props.shippingAccount && props.shippingAccount.name,
    });
  }
};

const resendPin = () => {
  if (selectedPinMethod.value) {
    emit("resend-pin-code", {
      id: props.shippingAccount && props.shippingAccount.id,
      carrier: "fedex_v2",
      auth: {
        pin_option: selectedPinMethod.value,
        regenerate_pin: true,
      },
      name: props.shippingAccount && props.shippingAccount.name,
    });
    showResendOptions.value = false;
    selectedPinMethod.value = "";
  }
};

const onClose = () => {
  emit("close");
  pinCode.value = "";
  showResendOptions.value = false;
  selectedPinMethod.value = "";
};
</script>
